import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, CheckCircle2, X } from 'lucide-react';
import type { ConteoDeclaradoVista, EstadoJornadaVista } from '@shared/types';
import { leerDineroTecleado } from '@shared/dineroTecleado';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { useStepUp } from '@renderer/hooks/useStepUp';
import { StepUpModal } from '@renderer/components/auth/StepUpModal';
import { EVENTO_SIN_CONCLUIR_ABIERTA, EVENTO_SIN_CONCLUIR_RESUELTO } from './PedidosSinConcluir';

/**
 * Sprint 160 — **Cerrar ayer en un toque.**
 *
 * La dueña cuenta el dinero antes de irse, pero no lo registraba: el cierre llegaba de noche, con
 * cuatro pasos y el cansancio encima. Su sistema anterior le preguntaba al abrir el día siguiente,
 * y eso es lo que se hace aquí: al entrar, una sola tarjeta con un número que escribir.
 *
 * Tres salidas, y ninguna le impide vender:
 *  - **Cerrar la caja** con lo que contó → el servidor dice si cuadra, falta o sobra.
 *  - **No lo conté** → se cierra igual, marcado sin conteo. No se inventa nada.
 *  - **Ahora no** → la tarjeta se vuelve una barra delgada arriba, que puede tocar cuando quiera.
 *
 * Todas las cifras vienen del servidor (Sprint 70): la pantalla no calcula dinero.
 */

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** "de ayer, sábado 17" o "del domingo 12". */
export function etiquetaDeLaCaja(desdeIso: string, ahora: Date = new Date()): string {
  const d = new Date(desdeIso);
  const ayer = new Date(ahora);
  ayer.setDate(ahora.getDate() - 1);
  const nombre = `${DIAS[d.getDay()]} ${d.getDate()}`;
  return d.toDateString() === ayer.toDateString() ? `de ayer, ${nombre}` : `del ${nombre}`;
}

const clavePospuesto = (corteId: number) => `cerrar-ayer-pospuesto-${corteId}`;

export function CerrarAyer() {
  const esDueno = useSessionStore((s) => s.sesion?.rol === 'dueño');
  const negocio = useNegocioStore((s) => s.negocio);
  const simbolo = negocio?.monedaSimbolo ?? '$';
  const navigate = useNavigate();
  const [estado, setEstado] = useState<EstadoJornadaVista['pendiente']>(null);
  const [abierta, setAbierta] = useState(false);
  const [contado, setContado] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ConteoDeclaradoVista | null>(null);
  const [porConfirmar, setPorConfirmar] = useState<number | null>(null);
  const [intentado, setIntentado] = useState(false);
  // Sprint 164 — Mientras "pedidos sin concluir" pregunta, esta tarjeta espera: primero se anota, luego se cuenta.
  const [otraPregunta, setOtraPregunta] = useState(false);
  const enviandoRef = useRef(false);
  const stepUp = useStepUp<ConteoDeclaradoVista>();
  const $ = (c: number) => formatPrecio(c, simbolo);

  const consultar = useCallback(async () => {
    const r = await window.api.corte.jornada();
    if (!r.ok) return; // si falla, no se interrumpe a nadie: se vuelve a preguntar después
    const p = r.data.pendiente;
    setEstado(p);
    if (p) setAbierta(localStorage.getItem(clavePospuesto(p.corteId)) !== new Date().toDateString());
  }, []);

  useEffect(() => {
    if (!esDueno) return;
    void consultar();
    // Una pestaña abierta toda la noche también tiene que enterarse de que ya es otro día.
    const alVolver = () => {
      if (document.visibilityState === 'visible') void consultar();
    };
    document.addEventListener('visibilitychange', alVolver);
    // Sprint 164 — Un pedido de ayer que se anotó como pagado cambia lo que debería haber en la caja.
    const alResolver = () => void consultar();
    window.addEventListener(EVENTO_SIN_CONCLUIR_RESUELTO, alResolver);
    const alOtra = (e: Event) => setOtraPregunta((e as CustomEvent<boolean>).detail === true);
    window.addEventListener(EVENTO_SIN_CONCLUIR_ABIERTA, alOtra);
    const cada = window.setInterval(() => void consultar(), 30 * 60_000);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener(EVENTO_SIN_CONCLUIR_RESUELTO, alResolver);
      window.removeEventListener(EVENTO_SIN_CONCLUIR_ABIERTA, alOtra);
      window.clearInterval(cada);
    };
  }, [esDueno, consultar]);

  if (!esDueno || (!estado && !resultado)) return null;

  const lectura = leerDineroTecleado(contado);
  const ilegible = !lectura.ok && lectura.motivo === 'no_es_numero';

  const declarar = async (montoContado: number | null) => {
    if (!estado || enviandoRef.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    setError(null);
    const r = await stepUp.ejecutar(() =>
      window.api.corte.declararConteo({ corteId: estado.corteId, montoContado }),
    );
    enviandoRef.current = false;
    setEnviando(false);
    if (r.ok) {
      setResultado(r.data);
      setEstado(null);
      setContado('');
      setPorConfirmar(null);
      setIntentado(false);
      return;
    }
    if (r.codigo !== 'STEP_UP_REQUIRED' && r.codigo !== 'CANCELADO') {
      setError(traducirErrorBackend(r.error, r.codigo));
      void consultar();
    }
  };

  const posponer = () => {
    if (estado) localStorage.setItem(clavePospuesto(estado.corteId), new Date().toDateString());
    setAbierta(false);
  };

  // ------------------------------------------------------------------ resultado
  /*
    Revisión ux-master S160: la diferencia se dice con el hecho, no con la culpa. Una diferencia
    grande casi siempre es dinero que salió para pagar algo o que la dueña se llevó, no un robo; el
    triángulo y "Faltan $9,295" en grande la acusaban de algo que ella misma hizo.
  */
  if (resultado) {
    const d = resultado.diferencia;
    return (
      <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="cerrar-ayer-res">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
          {d === null ? (
            <h2 id="cerrar-ayer-res" className="text-2xl font-bold text-slate-900">Listo: la caja quedó cerrada</h2>
          ) : d === 0 ? (
            <>
              <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" aria-hidden />
              <h2 id="cerrar-ayer-res" className="mt-2 text-3xl font-bold text-emerald-700">¡Cuadra!</h2>
            </>
          ) : (
            <h2 id="cerrar-ayer-res" className="text-3xl font-bold text-slate-900">Contaste {$(resultado.contado ?? 0)}</h2>
          )}
          <p className="mt-3 text-base text-slate-700">
            {d === null
              ? 'Se cerró sin conteo. Mañana te pregunto por la caja de hoy.'
              : d === 0
                ? `Contaste ${$(resultado.contado ?? 0)}, justo lo que debías tener.`
                : `Deberías tener ${$(resultado.efectivoEsperado)}.`}
          </p>
          {d !== null && d !== 0 && (
            <>
              <p className="mt-2 text-xl font-semibold text-amber-800">
                {d < 0 ? `Hay ${$(-d)} menos` : `Hay ${$(d)} de más`}
              </p>
              {d < 0 && (
                <p className="mt-2 text-sm text-slate-700">
                  Si sacaste dinero para pagar algo o te llevaste una parte, la próxima vez anótalo como salida en Corte de caja y te va a cuadrar.
                </p>
              )}
            </>
          )}
          <button autoFocus className="btn-primary btn-lg mt-6 min-h-[48px] w-full" onClick={() => setResultado(null)}>
            Listo
          </button>
        </div>
      </div>
    );
  }

  if (!estado) return null;
  if (otraPregunta && abierta) return null;
  const etiqueta = etiquetaDeLaCaja(estado.desde);

  // ------------------------------------------------------------------ barra (pospuesta)
  if (!abierta) {
    return (
      /*
        Franja dentro del contenido, no flotante: flotando arriba tapaba el nombre de quien está
        dentro y los botones del encabezado (visto en el recorrido con Playwright, 1366×768).
      */
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 py-1 pl-4 pr-1">
        <Wallet className="h-5 w-5 text-amber-700" aria-hidden />
        <span className="flex-1 text-sm font-medium text-amber-900">Falta cerrar la caja {etiqueta}</span>
        <button className="min-h-[44px] rounded-lg bg-amber-700 px-4 text-sm font-semibold text-white hover:bg-amber-800" data-uso="caja:abrir-desde-franja" onClick={() => setAbierta(true)}>
          Cerrarla
        </button>
      </div>
    );
  }

  // ------------------------------------------------------------------ la pregunta
  const sinCobrar = estado.pedidosSinCobrar;
  const mostrarIlegible = ilegible && intentado;
  /*
    Revisión ux-master S160 (bloqueante): un dedazo —2,500 en vez de 12,500— quedaba grabado para
    siempre, porque un conteo declarado no se corrige. Si lo contado no cuadra, se pregunta una vez.
    Sólo repite las dos cifras del servidor: no resta nada en la pantalla (Sprint 70).
  */
  const confirmarSiNoCuadra = () => {
    setIntentado(true);
    if (!lectura.ok) return;
    if (lectura.centavos === estado.efectivoEsperado) void declarar(lectura.centavos);
    else setPorConfirmar(lectura.centavos);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cerrar-ayer-titulo"
        onKeyDown={(e) => {
          if (e.key === 'Escape') posponer();
        }}
      >
        <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
          <button className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100" onClick={posponer} aria-label="Ahora no">
            <X className="h-6 w-6" />
          </button>

          {porConfirmar === null ? (
            <>
              <div className="flex items-center gap-3 pr-10">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Wallet className="h-7 w-7" aria-hidden />
                </div>
                <p className="text-base text-slate-700">
                  La caja {etiqueta}. Vendiste {$(estado.totalVentas)}.
                </p>
              </div>

              <h2 id="cerrar-ayer-titulo" className="mt-4 text-2xl font-bold text-slate-900">
                En efectivo deberías tener {$(estado.efectivoEsperado)}
              </h2>

              <label htmlFor="cerrar-ayer-contado" className="mt-5 block text-lg font-semibold text-slate-900">
                ¿Cuánto efectivo contaste?
              </label>
              {estado.montoInicial > 0 && (
                <p id="cerrar-ayer-ayuda" className="text-sm text-slate-700">
                  Cuenta todo lo que hay en la caja, también los {$(estado.montoInicial)} de cambio.
                </p>
              )}
              <div className={`mt-2 flex items-center rounded-xl border-2 ${mostrarIlegible ? 'border-red-500' : 'border-slate-300'} focus-within:border-primary`}>
                <span className="pl-4 text-2xl text-slate-600">{simbolo}</span>
                <input
                  id="cerrar-ayer-contado"
                  inputMode="decimal"
                  autoFocus
                  autoComplete="off"
                  aria-invalid={mostrarIlegible}
                  aria-describedby={[estado.montoInicial > 0 ? 'cerrar-ayer-ayuda' : '', mostrarIlegible ? 'cerrar-ayer-error' : ''].filter(Boolean).join(' ') || undefined}
                  className="w-full rounded-xl border-0 bg-transparent px-3 py-3 text-3xl font-semibold tracking-wide text-slate-900 shadow-none outline-none ring-0 placeholder:text-lg placeholder:font-normal placeholder:text-slate-500 focus:border-0 focus:outline-none focus:ring-0"
                  value={contado}
                  onChange={(e) => setContado(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmarSiNoCuadra();
                  }}
                  placeholder="Escribe lo que contaste"
                />
              </div>
              {mostrarIlegible && (
                <p id="cerrar-ayer-error" className="mt-1 text-sm text-red-700">Escribe sólo la cantidad, por ejemplo 2500.</p>
              )}

              {sinCobrar.length > 0 && (
                <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <p>
                    {sinCobrar.length === 1 ? 'Quedó 1 pedido sin cobrar' : `Quedaron ${sinCobrar.length} pedidos sin cobrar`} (
                    {$(sinCobrar.reduce((s, p) => s + p.total, 0))}). Puedes cerrar igual y revisarlos después.
                  </p>
                  <button
                    className="mt-2 min-h-[44px] rounded-lg border border-amber-400 bg-white px-3 font-semibold text-amber-900 hover:bg-amber-100"
                    onClick={() => {
                      posponer();
                      navigate('/pedidos');
                    }}
                  >
                    Ver esos pedidos
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  {error}
                </div>
              )}

              <button
                className="btn-primary btn-lg mt-6 min-h-[52px] w-full text-lg disabled:opacity-60"
                disabled={enviando || contado.trim() === ''}
                onClick={confirmarSiNoCuadra}
                data-uso="caja:cerrar-ayer"
              >
                {enviando ? 'Cerrando…' : 'Cerrar la caja'}
              </button>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button className="min-h-[48px] rounded-xl border-2 border-slate-500 font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60" disabled={enviando} data-uso="caja:no-lo-conte" onClick={() => void declarar(null)}>
                  No lo conté
                </button>
                <button className="min-h-[48px] rounded-xl font-semibold text-slate-700 hover:bg-slate-100" data-uso="caja:ahora-no" onClick={posponer}>
                  Ahora no
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 id="cerrar-ayer-titulo" className="pr-10 text-2xl font-bold text-slate-900">¿Contaste todo?</h2>
              <p className="mt-4 text-lg text-slate-800">
                Contaste <strong>{$(porConfirmar)}</strong>.
              </p>
              <p className="text-lg text-slate-800">
                Deberías tener <strong>{$(estado.efectivoEsperado)}</strong>.
              </p>
              <p className="mt-2 text-sm text-slate-700">Ya guardado, este número no se puede cambiar.</p>
              <button
                autoFocus
                className="btn-primary btn-lg mt-6 min-h-[52px] w-full text-lg disabled:opacity-60"
                disabled={enviando}
                onClick={() => void declarar(porConfirmar)}
              >
                {enviando ? 'Cerrando…' : 'Sí, así queda'}
              </button>
              <button className="mt-3 min-h-[48px] w-full rounded-xl border-2 border-slate-300 font-semibold text-slate-800 hover:bg-slate-50" onClick={() => setPorConfirmar(null)}>
                Corregir
              </button>
            </>
          )}
        </div>
      </div>
      <StepUpModal abierto={stepUp.modalAbierto} onConfirmado={stepUp.onConfirmado} onCancelar={stepUp.onCancelar} />
    </>
  );
}
